import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Breadcrumbs,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
} from "@mui/material";
import Link from "@mui/material/Link";
import axios from "axios";
import moment from "moment";
import Layout from "../../layout/Layout";
import { config, advanceLedgerAPI, userData } from "../../config";

// Super-admin / payer view of outstanding advance balances -- the advance
// analogue of Cash in Hand. Pick an employee from the outstanding worklist to
// see their running balance, per-advance breakdown, and full ledger. Read-only:
// every movement (disbursement / recovered EMI) is posted automatically by the
// disburse + payroll flows. All calls hit POST /advance-ledger:
//   1 balance, 2 ledger, 4 per-advance breakdown, 5 outstanding worklist.
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

// Debit types reduce what the employee owes (repayment = good/green).
const DEBIT = ["deduction", "writeoff", "settlement"];
const TYPE_LABEL = {
  disbursement: "Advance disbursed",
  deduction: "EMI recovered",
  writeoff: "Written off",
  settlement: "Settled",
  adjustment: "Adjustment",
};

function AdvanceBalances() {
  const [worklist, setWorklist] = useState([]);
  const [empId, setEmpId] = useState(0);
  const [empName, setEmpName] = useState("");
  const [balance, setBalance] = useState(0);
  const [breakdown, setBreakdown] = useState([]);
  const [ledger, setLedger] = useState([]);

  const isAllowed =
    Number(userData?.role_id) === 34 || Number(userData?.role_id) === 35;

  const post = (body) => {
    config.method = "POST";
    config.url = advanceLedgerAPI;
    config.data = JSON.stringify(body);
    return axios(config);
  };

  const tempRefs = useRef(false);
  useEffect(() => {
    if (tempRefs.current === false && isAllowed) {
      post({ intType: 5 })
        .then((r) => setWorklist(r.data.result || []))
        .catch((e) => console.log(e));
      tempRefs.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (row) => {
    setEmpId(row.employee_id);
    setEmpName(row.name || "");
    setBalance(0);
    setBreakdown([]);
    setLedger([]);
    post({ intType: 1, intEmployeeID: row.employee_id })
      .then((r) => {
        const b = r.data.result && r.data.result[0];
        setBalance(b ? Number(b.balance) || 0 : 0);
      })
      .catch((e) => console.log(e));
    post({ intType: 4, intEmployeeID: row.employee_id })
      .then((r) => setBreakdown(r.data.result || []))
      .catch((e) => console.log(e));
    post({ intType: 2, intEmployeeID: row.employee_id, intLimit: 100, intOffset: 0 })
      .then((r) => setLedger(r.data.result || []))
      .catch((e) => console.log(e));
  };

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid textAlign="center" item className="form-lable" xs={12} lg={6} md={6}>
            <Typography className="page_title" textAlign="left">
              Advance Balances
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Advance Balances</Typography>
            </Breadcrumbs>
          </Grid>
        </Grid>

        <div className="empty-space-40"></div>

        {!isAllowed ? (
          <Typography color="error" sx={{ mt: 2 }}>
            You are not authorized to view this page.
          </Typography>
        ) : (
          <Grid container spacing={2} padding={`0px 15px 0px 0px`}>
            {/* Outstanding worklist */}
            <Grid item xs={12} lg={4} md={5}>
              <Typography sx={{ fontWeight: 700, mb: 1, color: "#334" }}>
                Outstanding ({worklist.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f4f6fb" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Owed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {worklist.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 3, color: "text.secondary" }}>
                          Nothing outstanding.
                        </TableCell>
                      </TableRow>
                    ) : (
                      worklist.map((row) => (
                        <TableRow
                          key={row.employee_id}
                          hover
                          selected={row.employee_id === empId}
                          sx={{ cursor: "pointer" }}
                          onClick={() => handleSelect(row)}
                        >
                          <TableCell>{row.name || `#${row.employee_id}`}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: "#c62828" }}>
                            {inr(row.balance)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Detail */}
            <Grid item xs={12} lg={8} md={7}>
              {empId > 0 ? (
                <>
                  <Card variant="outlined" sx={{ borderRadius: 2, backgroundColor: "#f4f6fb", mb: 2 }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography sx={{ fontSize: 12, color: "#7d8f8e", fontWeight: 600 }}>
                        Outstanding advance · {empName}
                      </Typography>
                      <Typography sx={{ fontSize: 22, fontWeight: 800, color: balance > 0 ? "#c62828" : "#2E7D32" }}>
                        {inr(balance)}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: "#7d8f8e" }}>
                        {balance > 0 ? "Yet to be recovered from salary" : "Fully repaid"}
                      </Typography>
                    </CardContent>
                  </Card>

                  {breakdown.length > 0 && (
                    <>
                      <Typography sx={{ fontWeight: 700, mb: 1, color: "#334" }}>
                        Advances
                      </Typography>
                      <Grid container spacing={1} sx={{ mb: 2 }}>
                        {breakdown.map((a) => (
                          <Grid item key={a.advance_payment_id}>
                            <Chip
                              variant="outlined"
                              color={Number(a.balance) > 0 ? "warning" : "success"}
                              label={`${a.payment_type === "salaryAdvance" ? "Salary" : "Personal"} ${inr(a.amount)} · bal ${inr(a.balance)}`}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}

                  <Typography sx={{ fontWeight: 700, mb: 1.5, color: "#334" }}>
                    Ledger
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f4f6fb" }}>
                          <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Balance</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Cycle</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ledger.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>
                              No entries yet.
                            </TableCell>
                          </TableRow>
                        ) : (
                          ledger.map((row) => {
                            const down = DEBIT.includes(row.entry_type);
                            return (
                              <TableRow key={row.id} hover>
                                <TableCell>
                                  {row.created_at ? moment(row.created_at).format("DD MMM YYYY, hh:mm A") : "—"}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={TYPE_LABEL[row.entry_type] || row.entry_type}
                                    color={down ? "success" : "warning"}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell align="right" sx={{ color: down ? "#2E7D32" : "#c62828", fontWeight: 700 }}>
                                  {down ? "−" : "+"}
                                  {inr(Math.abs(Number(row.amount) || 0))}
                                </TableCell>
                                <TableCell align="right">{inr(row.balance_after)}</TableCell>
                                <TableCell>
                                  {row.cycle_month ? moment(row.cycle_month).format("MMM YY") : "—"}
                                </TableCell>
                                <TableCell>{row.note || "—"}</TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Typography sx={{ mt: 2, color: "text.secondary" }}>
                  Select an employee to see their advance balance and ledger.
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
    </Layout>
  );
}

export default AdvanceBalances;
